<template>
    <section class="section public-fights-queue-app">
        <div class="container-fluid">
            <PublicFightsQueueHeader />

            <div v-if="initDone" class="fights-grid">
                <div class="columns is-multiline is-mobile">
                    <div v-for="mat in store.matsForGivenPage" :key="mat.id" class="column mat-column">
                        <div class="mat-header">
                            <h2 class="mat-name">{{ mat.name }}</h2>
                        </div>

                        <div v-if="hasFightsForMat(mat.id)" class="fights-list">
                            <SingleFight
                                v-for="(fightWithTime, index) in store.fightsForMat[mat.id]"
                                :key="fightWithTime.fight.id"
                                :fight="fightWithTime.fight"
                                :start-time="fightWithTime.startTime"
                                :fight-index="index"
                                :event-timezone="store.eventTimezone"
                                :fight-queue-statuses="store.fightQueueStatuses"
                                :tracked-time-for-fights="store.trackedTimeForFights"
                                :tracked-round-statuses-for-fights="store.trackedRoundStatusesForFights"
                                :show-category-header="true"
                                :show-winner-and-loser="false"
                                :show-branch="false"
                                :color-schema="store.colorSchema"
                                :disable-category-link="true"
                            />
                        </div>
                        <div v-else class="empty-state">
                            <p>{{ translate("flash_messages.no_fights_on_mat") }}</p>
                        </div>
                    </div>
                    <div v-if="store.matsForGivenPage.length === 0" class="column is-12 has-text-centered is-size-5">
                        <p>{{ translate("common.no_results") }}</p>
                    </div>
                </div>
            </div>

            <div v-else class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { usePublicFightsQueueStore } from "./publicFightsQueueStore";
    import { onMounted, onUnmounted, ref, watch } from "vue";
    import { clearIntervalAsync, setIntervalAsync } from "set-interval-async";
    import { createClient } from "../Common/websocketClient";
    import { handleError } from "../Common/errorHandler";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { getScoreboardMatChannel, SCOREBOARD_CHANNEL } from "../Common/websocketUtils";
    import PublicFightsQueueHeader from "./PublicFightsQueueHeader.vue";
    import SingleFight from "../Common/Fights/SingleFight.vue";
    import { translate } from "../Common/translations";
    import { WebSocketScoreboardMessage } from "../Scoreboard/Common/types";
    import history from "../Common/history";
    import { normalizeLiveFightScore } from "../Public/liveFightScores";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
        eventName: string;
    }>();

    const historyInstance = history.getInstance(window);
    const store = usePublicFightsQueueStore();
    const initDone = ref(false);

    // Initialize store with props
    store.eventId = props.eventId;
    store.eventTimezone = props.eventTimezone;
    store.eventName = props.eventName;

    // Parse URL query params and load into store
    const urlParams = new URLSearchParams(window.location.search);
    store.loadFromQueryParams(urlParams);

    // Watch page number changes and sync with query params
    watch(
        () => store.pageNumber,
        () => {
            historyInstance.setQueryParam("page", store.pageNumber);
        },
    );

    // TIMER HANDLING

    let interval: ReturnType<typeof setIntervalAsync> | null = null;

    const timerHandler = async () => {
        store.tickTimer();
    };

    function initializeTimerLoop() {
        if (!interval) {
            interval = setIntervalAsync(timerHandler, 1000);
            store.startTimer();
        }
    }

    function stopTimerLoop() {
        if (interval) {
            clearIntervalAsync(interval);
            interval = null;
            store.stopTimer();
        }
    }

    // Pause timer when page is hidden, resume on focus
    const handleVisibilityChange = () => {
        if (document.hidden) {
            stopTimerLoop();
        } else {
            initializeTimerLoop();
            // Refresh immediately when returning to page
            store.refreshAllMats().catch(handleError);
        }
    };

    onMounted(() => {
        document.addEventListener("visibilitychange", handleVisibilityChange);
    });

    onUnmounted(() => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        stopTimerLoop();
    });

    // WEBSOCKETS

    const websocketClient = createClient();
    store.websocketClient = websocketClient;
    websocketClient.connect(null);

    function joinChannelForMat(matId: number) {
        websocketClient.joinChannel(getScoreboardMatChannel(matId));
    }

    function leaveChannelForMat(matId: number) {
        websocketClient.leaveChannel(getScoreboardMatChannel(matId));
    }

    websocketClient.onopen = () => {
        // Join channels for mats on current page
        store.matsForGivenPage.forEach(mat => {
            joinChannelForMat(mat.id);
        });
    };

    websocketClient.onmessage = message => {
        const data: WebSocketScoreboardMessage = JSON.parse(message.data);
        if (!data.channel) {
            return;
        }

        // Update tracked time for fights
        if (data.fightId && data.internalTime !== undefined && data.channel.includes(SCOREBOARD_CHANNEL)) {
            store.updateTrackedTime(data.fightId, data.internalTime);

            const normalizedScore = normalizeLiveFightScore(data);
            if (normalizedScore?.roundStatus) {
                store.updateTrackedRoundStatus(data.fightId, normalizedScore.roundStatus);
            } else {
                store.removeTrackedRoundStatus(data.fightId);
            }

            return;
        }
    };

    // Watch for page changes and update WebSocket channels
    watch(
        () => store.matsForGivenPage,
        (newMats, oldMats) => {
            const oldMatIds = (oldMats || []).map(mat => mat.id);
            const newMatIds = newMats.map(mat => mat.id);

            const removedMatIds = oldMatIds.filter(id => !newMatIds.includes(id));
            const addedMatIds = newMatIds.filter(id => !oldMatIds.includes(id));

            removedMatIds.forEach(matId => {
                leaveChannelForMat(matId);
            });

            addedMatIds.forEach(matId => {
                joinChannelForMat(matId);
            });
        },
    );

    onUnmounted(() => {
        websocketClient.close();
    });

    // INITIALIZATION

    onMounted(async () => {
        await store
            .refreshAllMats()
            .then(() => {
                initializeTimerLoop();
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    });

    // HELPERS

    function hasFightsForMat(matId: number): boolean {
        const fights = store.fightsForMat[matId];
        return fights && fights.length > 0;
    }
</script>
